import { useState, type CSSProperties } from 'react';
import { Alert, Button, theme, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { PageHeader } from '../../../components/PageHeader';
import { EmptyState } from '../../../components/EmptyState';
import { DataTable } from '../../../components/DataTable';
import { KpiCard } from '../../dashboard/components/KpiCard';
import { useHasPermission } from '../../../auth/useHasPermission';
import { Permission } from '@aid/shared';
import { useT } from '../../../locales';
import { useDebouncedValue } from '../../../lib/useDebouncedValue';
import { paginationProps } from '../../../lib/paginacion';
import { useServerPaginacion } from '../../../lib/server-sort';
import { CORTE_MORA_EXPORT_FORMAT } from '@aid/shared';
import type { CorteMoraClienteRow, CorteMoraExportFormat, CorteMoraFilters } from '@aid/shared';
import { useCorteMoraList, useCorteMoraStats, useExportCorteMora } from './api/useCorteMora';
import { CorteMoraToolbar } from './components/CorteMoraToolbar';
import { createCorteMoraTableColumns } from './components/CorteMoraTableColumns';
import { downloadBlob } from '../../../lib/download';
import { EMPTY_CORTE_MORA_FILTERS } from './types';
import type { CorteMoraFilterState } from './types';
import './corte-mora.css';

const KPI_GRID_STYLE: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 16,
  marginBottom: 16,
};

const CONTEXT_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  padding: '12px 14px',
  marginBottom: 18,
  borderRadius: 8,
};

const EXPORT_FILE_NAMES = {
  [CORTE_MORA_EXPORT_FORMAT.PDF]: 'corte-por-mora.pdf',
  [CORTE_MORA_EXPORT_FORMAT.XLSX]: 'corte-por-mora.xlsx',
} as const;

const { Text } = Typography;

export function CorteMoraPage() {
  const t = useT();
  const { token } = theme.useToken();
  const canReadClient = useHasPermission(Permission.CLIENTES_READ);
  const [filters, setFilters] = useState<CorteMoraFilterState>(EMPTY_CORTE_MORA_FILTERS);
  const { page, pageSize, sort, handleChange, setPage } =
    useServerPaginacion<CorteMoraClienteRow>(20);
  const debouncedSearch = useDebouncedValue(filters.search);
  const queryFilters: CorteMoraFilters = {
    ...filters,
    search: debouncedSearch.trim() || undefined,
  };
  const exportFilters: CorteMoraFilters = {
    ...filters,
    search: filters.search.trim() || undefined,
  };

  const { data, isLoading, isFetching, error, refetch } = useCorteMoraList({
    ...queryFilters,
    limit: pageSize,
    offset: (page - 1) * pageSize,
    ...sort,
  });

  const {
    data: stats,
    isLoading: statsLoading,
    isFetching: statsFetching,
    error: statsError,
    refetch: refetchStats,
  } = useCorteMoraStats(queryFilters);
  const exportMutation = useExportCorteMora();
  const [exportingFormat, setExportingFormat] = useState<CorteMoraExportFormat | null>(null);

  const rows = data?.data ?? [];
  const hasFilters =
    filters.search.trim() !== '' ||
    filters.idCliente !== undefined ||
    filters.tipoFactura !== undefined ||
    filters.fechaDesde !== undefined ||
    filters.fechaHasta !== undefined;
  const statsEmpty = (statsLoading || statsFetching) && !stats;

  const updateFilters = (next: Partial<CorteMoraFilterState>) => {
    setFilters((current) => ({ ...current, ...next }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({ ...EMPTY_CORTE_MORA_FILTERS });
    setPage(1);
  };

  const handleExport = async (format: CorteMoraExportFormat): Promise<void> => {
    setExportingFormat(format);
    exportMutation.reset();
    try {
      const blob = await exportMutation.mutateAsync({ format, filters: exportFilters });
      downloadBlob(blob, EXPORT_FILE_NAMES[format]);
    } finally {
      setExportingFormat(null);
    }
  };

  const columns = createCorteMoraTableColumns({
    t,
    canReadClient,
    primaryColor: token.colorPrimaryActive,
    linkColor: token.colorPrimary,
    secondaryColor: token.colorTextSecondary,
    sort,
  });

  return (
    <div className="corte-mora-page">
      <PageHeader
        title={t('pages.corteMora.title')}
        subtitle={t('pages.corteMora.subtitle')}
        subtitleStyle={{ color: token.colorTextSecondary }}
      />

      <div
        role="note"
        style={{
          ...CONTEXT_STYLE,
          border: `1px solid ${token.colorInfoBorder}`,
          background: token.colorInfoBg,
        }}
      >
        <Text strong style={{ color: token.colorInfoText }}>
          {t('pages.corteMora.criteriaBadge')}
        </Text>
        <Text style={{ color: token.colorText }}>{t('pages.corteMora.criteriaDescription')}</Text>
      </div>

      <div style={KPI_GRID_STYLE} data-testid="corte-mora-kpi-grid">
        <KpiCard
          label={t('pages.corteMora.kpis.clients')}
          value={String(stats?.clientesCandidatos ?? 0)}
          formatValue={false}
          accent="clientes"
          empty={statsEmpty}
          compact
        />
        <KpiCard
          label={t('pages.corteMora.kpis.invoices')}
          value={String(stats?.facturasPendientes ?? 0)}
          formatValue={false}
          accent="cobros"
          empty={statsEmpty}
          compact
        />
        <KpiCard
          label={t('pages.corteMora.kpis.debt')}
          value={stats?.deudaPendiente ?? '0.00'}
          accent="mora"
          empty={statsEmpty}
          compact
        />
      </div>

      {statsError && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          title={t('pages.corteMora.statsError')}
          action={
            <Button type="link" size="small" onClick={() => void refetchStats()}>
              {t('common.retry')}
            </Button>
          }
        />
      )}

      <CorteMoraToolbar
        t={t}
        filters={filters}
        resultTotal={data?.total ?? 0}
        hasFilters={hasFilters}
        exportingFormat={exportingFormat}
        onSearchChange={(value) => updateFilters({ search: value })}
        onClientChange={(value) => updateFilters({ idCliente: value })}
        onTipoFacturaChange={(value) => updateFilters({ tipoFactura: value })}
        onDateRangeChange={(value) =>
          updateFilters({
            fechaDesde: value?.[0] ?? undefined,
            fechaHasta: value?.[1] ?? undefined,
          })
        }
        onClear={clearFilters}
        onExport={(format) => void handleExport(format)}
      />

      {exportMutation.error && (
        <Alert
          type="error"
          showIcon
          closable
          style={{ marginBottom: 16 }}
          title={t('pages.corteMora.exportError')}
          onClose={() => exportMutation.reset()}
        />
      )}

      {error ? (
        <Alert
          type="error"
          showIcon
          title={t('pages.corteMora.errorLoad')}
          action={
            <Button
              type="link"
              icon={<ReloadOutlined aria-hidden />}
              onClick={() => void refetch()}
            >
              {t('common.retry')}
            </Button>
          }
        />
      ) : (
        <DataTable<CorteMoraClienteRow>
          columns={columns}
          dataSource={rows}
          rowKey="idCliente"
          loading={isLoading || isFetching}
          locale={{
            emptyText: (
              <EmptyState
                title={t('pages.corteMora.emptyTitle')}
                description={t('pages.corteMora.emptyDescription')}
              />
            ),
          }}
          pagination={paginationProps(t, {
            page,
            pageSize,
            total: data?.total ?? 0,
            showSizeChanger: false,
          })}
          onChange={handleChange}
        />
      )}
    </div>
  );
}
